import { Component, inject } from "@angular/core";
import { ICellRendererAngularComp } from "ag-grid-angular";
import { ICellRendererParams } from "ag-grid-community";
import { DecimalPipe } from "@angular/common";
import { PluginConfigService } from "@bitwarden/web-vault/app/services/store/data-plugin/plugin-config.service";
import { isGroceryTransaction } from "@bitwarden/web-vault/app/services/store/data-plugin/transaction-plugin.utils";

interface AmountRendererParams extends ICellRendererParams {
  showBalanceDetails?: boolean;
  showGroceryIcon?: boolean;
}

@Component({
  selector: "app-balance-details-renderer",
  templateUrl: "./balance-details-renderer.component.html",
  providers: [DecimalPipe],
})
export class BalanceDetailsRendererComponent implements ICellRendererAngularComp {
  balance!: number;
  balanceDiff!: number;
  description!: string;
  symbol!: string;
  showGroceryIcon = false;
  private transactionPluginEnabled = false;
  // A balance-alignment row is identified by transactionView.bankImportedBalance, not by
  // description text - a user-noted alignment row keeps the note as its description, so
  // matching on description would miss it (see #4104).
  isBalanceAlignment: boolean = false;
  decimalPipe = inject(DecimalPipe);
  private pluginConfigService = inject(PluginConfigService);
  private params!: AmountRendererParams;

  agInit(params: AmountRendererParams): void {
    this.params = params;
    this.setData(params);
  }

  setData(params: AmountRendererParams) {
    this.balance = params.data.balance || 0;
    this.balanceDiff = params.data.amount || 0;
    this.description = params.data.description || "";
    this.symbol = params.data.symbol || "";
    this.showGroceryIcon = params.showGroceryIcon ?? false;
    this.isBalanceAlignment = params.data.transactionView?.bankImportedBalance != null;
    this.transactionPluginEnabled = this.pluginConfigService.isTransactionPluginEnabled();
  }

  refresh(params: AmountRendererParams): boolean {
    this.params = params;
    this.setData(params);
    return true;
  }

  get formattedBalanceDiff(): string {
    const diff = this.balanceDiff ?? 0;
    const sign = diff < 0 ? "-" : "";
    const abs = Math.abs(diff);
    const formatted = this.decimalPipe.transform(abs, "1.2-2") || "0.00";
    return sign + formatted;
  }

  get showInfoIcon(): boolean {
    return (
      this.showGroceryIcon &&
      this.transactionPluginEnabled &&
      isGroceryTransaction(this.description)
    );
  }

  get isExpanded(): boolean {
    return this.params?.node?.expanded ?? false;
  }

  toggleDetail(event: MouseEvent): void {
    event.stopPropagation();
    this.params.node.setExpanded(!this.params.node.expanded);
    this.params.api.refreshCells({ rowNodes: [this.params.node], force: true });
  }
}
